import { useMemo, useState } from 'react';
import {
  useGetSalesChartQuery,
  useGetOrdersSummaryQuery,
  useGetOrdersChartQuery,
} from './dashboard.api';
import { addDays } from 'date-fns';
import styles from './dashboard.module.css';
import { Card, CardContent } from 'shared-components';
import { Button } from 'shared-components';
import { Input } from 'shared-components';
import { FormLayout } from 'shared-components';
import { FormGroup } from 'shared-components';
import { Label } from 'shared-components';
import { Fact } from '../fact/fact';
import { ShoppingBagIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { Select } from 'shared-components';
import ApexChart from 'react-apexcharts';

// Chart.register(CategoryScale);
// Chart.register(Filler);

export const Dashboard = () => {
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' }),
    []
  );
  const [dateRange, setDateRange] = useState<[Date, Date]>([
    addDays(new Date(), -30),
    new Date(),
  ]);
  const [salesGrouping, setSalesGrouping] = useState<'day' | 'month'>('day');
  const [_dateRange, _setDateRange] = useState<[Date, Date]>([
    addDays(new Date(), -30),
    new Date(),
  ]);
  const { data, isLoading, isFetching } = useGetSalesChartQuery({
    fromDate: dateRange[0],
    toDate: dateRange[1],
    groupBy: salesGrouping,
  });
  const {
    data: ordersData,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
  } = useGetOrdersChartQuery({
    fromDate: dateRange[0],
    toDate: dateRange[1],
    groupBy: salesGrouping,
  });
  const {
    data: orderSummaryData,
    isLoading: orderSummaryIsLoading,
    isFetching: orderSummaryIsFetching,
  } = useGetOrdersSummaryQuery({
    fromDate: dateRange[0],
    toDate: dateRange[1],
  });
  if (
    isLoading ||
    isFetching ||
    orderSummaryIsFetching ||
    orderSummaryIsLoading ||
    ordersLoading ||
    ordersFetching
  )
    return <h1>Loading...</h1>;

  return (
    <>
      <div className={styles.title}>
        <Card>
          <CardContent>
            <FormLayout className={styles.dates}>
              <FormGroup>
                <Label htmlFor="from">From</Label>
                <Input
                  id="from"
                  name="from"
                  type="date"
                  value={_dateRange[0].toLocaleDateString('en-CA')}
                  onChange={(e) =>
                    _setDateRange([new Date(e.target.value), dateRange[1]])
                  }
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="to">To</Label>
                <Input
                  id="to"
                  name="to"
                  type="date"
                  value={_dateRange[1].toLocaleDateString('en-CA')}
                  onChange={(e) =>
                    _setDateRange([dateRange[0], new Date(e.target.value)])
                  }
                />
              </FormGroup>
              <FormGroup>
                <Button
                  bigger={true}
                  className="w-100"
                  onClick={() => {
                    setDateRange(_dateRange);
                  }}
                >
                  Update
                </Button>
              </FormGroup>
            </FormLayout>
          </CardContent>
        </Card>
      </div>
      <div className={styles.chartsLayout}>
        <Fact
          title="Total Orders"
          value={orderSummaryData?.totalOrders.toString() ?? '0'}
          icon={<ShoppingBagIcon stroke="#3C50E0" height={22} width={22} />}
        />
        <Fact
          title="Total Spend"
          value={currencyFormatter.format(
            orderSummaryData?.totalOrderValue ?? (0 as number)
          )}
          icon={<ShoppingCartIcon stroke="#3C50E0" height={22} width={22} />}
        />

        <div
          style={{
            gridColumnStart: 1,
            gridColumnEnd: 3,
          }}
        >
          <Card>
            <CardContent>
              <div className={styles.chartTitle}>
                <h2>Sales</h2>
                <Select
                  value={salesGrouping}
                  onChange={(e) =>
                    setSalesGrouping(e.target.value as 'day' | 'month')
                  }
                >
                  <option value="day">Day</option>
                  <option value="month">Month</option>
                </Select>
              </div>
              <ApexChart
                options={{
                  colors: ['#3C50E0', '#80CAEE'],
                  chart: {
                    fontFamily: 'Satoshi, sans-serif',
                    height: 335,
                    type: 'area',
                    zoom: {
                      enabled: false,
                    },
                    dropShadow: {
                      enabled: true,
                      color: '#623CEA14',
                      top: 10,
                      blur: 4,
                      left: 0,
                      opacity: 0.1,
                    },
                    toolbar: {
                      show: false,
                    },
                  },
                  markers: {
                    size: 5,
                    colors: '#fff',
                    strokeColors: ['#3056D3', '#80CAEE'],
                    strokeWidth: 4,
                    strokeOpacity: 0.9,
                    strokeDashArray: 0,
                    fillOpacity: 1,
                    discrete: [],
                    hover: {
                      size: undefined,
                      sizeOffset: 5,
                    },
                  },
                  stroke: {
                    width: [2, 2],
                    curve: 'straight',
                  },
                  dataLabels: {
                    enabled: false,
                  },
                  fill: {
                    gradient: {
                      opacityFrom: 0.75,
                      opacityTo: 0.4,
                    },
                  },

                  // title: {
                  //   text: 'Fundamental Analysis of Stocks',
                  //   align: 'left',
                  // },
                  // subtitle: {
                  //   text: 'Price Movements',
                  //   align: 'left',
                  // },
                  tooltip: {
                    x: {
                      format:
                        salesGrouping === 'day' ? 'dd MMM yyyy' : 'MMM yyyy',
                    },
                  },
                  labels: data?.map((d) => d._id),
                  xaxis: {
                    type: 'datetime',
                    min: dateRange[0].getTime(),
                    max: dateRange[1].getTime(),
                  },
                  yaxis: [
                    {
                      max:
                        Math.max(
                          ...(data?.map((d) => d.daily_total_paid) as number[])
                        ) + 100,
                      tickAmount: 10,
                      labels: {
                        formatter: (value) => currencyFormatter.format(value),
                      },
                    },
                    {
                      opposite: true,
                      max:
                        Math.max(
                          ...(ordersData?.map(
                            (d) => d.daily_orders
                          ) as number[])
                        ) + 10,
                      tickAmount: 10,
                      // labels: {
                      //   formatter: (value) => currencyFormatter.format(value),
                      // },
                    },
                  ],
                  // legend: {
                  //   horizontalAlign: 'left',
                  // },
                }}
                series={[
                  {
                    name: 'Total Sales',
                    data: data?.map((d) => d.daily_total_paid) as number[],
                  },
                  {
                    name: 'Total Orders',
                    data: ordersData?.map((d) => d.daily_orders) as number[],
                  },
                ]}
                type="area"
                height={350}
              />
              {/* <Line
                options={{
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: (context) => {
                          // const label = context.dataset.label || '';
                          return currencyFormatter.format(context.parsed.y);
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) =>
                          currencyFormatter.format(value as number),
                      },
                    },
                  },
                }}
                data={{
                  labels: data?.map((d) => d._id),
                  datasets: [
                    {
                      label: 'Sales',
                      data: data?.map((d) => d.daily_total_paid),

                      tension: 0.1,
                      borderColor: 'rgb(60 80 224)',
                      backgroundColor: (context: ScriptableContext<'line'>) => {
                        const ctx = context.chart.ctx;
                        const gradient = ctx.createLinearGradient(0, 0, 0, 200);
                        gradient.addColorStop(0, 'rgba(60, 80, 224, 0.7)');
                        gradient.addColorStop(1, 'rgba(60, 80, 224, 0.2)');
                        return gradient;
                      },
                      fill: true,
                    },
                  ],
                }}
              /> */}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};
